import { useState, useEffect } from 'react';

/**
 * Hook to calculate and display idle/active time relative to current time
 * Updates every minute to keep the display fresh
 */
export function useIdleTime(lastSeenDate?: string | Date | null): string {
  const [relativeTime, setRelativeTime] = useState<string>('');

  useEffect(() => {
    const calculateRelativeTime = () => {
      if (!lastSeenDate) {
        return 'Unknown';
      }

      const lastSeen = typeof lastSeenDate === 'string' ? new Date(lastSeenDate) : lastSeenDate;
      const now = new Date();
      const diffMs = now.getTime() - lastSeen.getTime();
      const diffMinutes = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);

      // Active now (less than 1 minute)
      if (diffMinutes < 1) {
        return 'Active now';
      }

      // Minutes ago (1-59 minutes)
      if (diffMinutes < 60) {
        return `Active ${diffMinutes}m ago`;
      }

      // Hours ago (1-23 hours)
      if (diffHours < 24) {
        return `Active ${diffHours}h ago`;
      }

      // Days ago (1-6 days)
      if (diffDays < 7) {
        return `Active ${diffDays}d ago`;
      }

      // More than a week - show date
      return `Last seen ${lastSeen.toLocaleDateString()}`;
    };

    // Calculate immediately
    setRelativeTime(calculateRelativeTime());

    // Update every minute
    const interval = setInterval(() => {
      setRelativeTime(calculateRelativeTime());
    }, 60000); // 1 minute

    return () => clearInterval(interval);
  }, [lastSeenDate]);

  return relativeTime;
}

/**
 * Format a date to a relative time string (one-time calculation, no auto-update)
 */
export function formatRelativeTime(lastSeenDate?: string | Date | null): string {
  if (!lastSeenDate) {
    return 'Unknown';
  }

  const lastSeen = typeof lastSeenDate === 'string' ? new Date(lastSeenDate) : lastSeenDate;
  const now = new Date();
  const diffMs = now.getTime() - lastSeen.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) {
    return 'Active now';
  }

  if (diffMinutes < 60) {
    return `Active ${diffMinutes}m ago`;
  }

  if (diffHours < 24) {
    return `Active ${diffHours}h ago`;
  }

  if (diffDays < 7) {
    return `Active ${diffDays}d ago`;
  }

  return `Last seen ${lastSeen.toLocaleDateString()}`;
}
